import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useEffect } from "react";
import { ClipboardCheck, Camera, BarChart2, FileCheck } from "lucide-react";

const InspectionsUseCase = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <div className="relative bg-[#0077bb] py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative">
            <h1 className="text-4xl font-bold text-white mb-6">
              Digital Inspections & Quality Control
            </h1>
            <p className="text-xl text-blue-100 max-w-3xl">
              Transform your inspection processes with digital forms that streamline data collection, improve accuracy, and enable real-time reporting.
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-2 gap-12 items-center mb-20">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Streamline Your Inspection Processes
            </h2>
            <p className="text-lg text-gray-600 mb-6">
              Create and manage comprehensive inspection forms that help maintain quality standards across your organization:
            </p>
            <ul className="space-y-4 text-gray-600">
              <li className="flex items-start">
                <span className="mr-2">•</span>
                Conduct thorough inspections with customizable checklists
              </li>
              <li className="flex items-start">
                <span className="mr-2">•</span>
                Capture photos and annotations directly in forms
              </li>
              <li className="flex items-start">
                <span className="mr-2">•</span>
                Generate instant reports and analytics
              </li>
            </ul>
          </div>
          <div>
            <img
              src="https://images.unsplash.com/photo-1587293852726-70cdb56c2866"
              alt="Quality inspection process"
              className="rounded-lg shadow-xl"
            />
          </div>
        </div>

        {/* Key Features Section */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-center mb-12">
              Key Features
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white p-6 rounded-lg shadow-lg">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                  <ClipboardCheck className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold mb-4">Digital Forms</h3>
                <p className="text-gray-600">
                  Customizable inspection forms with smart validation and conditional logic.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-lg">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                  <Camera className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold mb-4">Photo Capture</h3>
                <p className="text-gray-600">
                  Integrated photo capture with annotation capabilities.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-lg">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                  <BarChart2 className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold mb-4">Analytics</h3>
                <p className="text-gray-600">
                  Comprehensive reporting and analytics dashboard.
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Common Use Cases Section */}
      <div className="bg-gray-50">
        <section className="py-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12">
            Common Use Cases
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white p-8 rounded-lg shadow">
              <h3 className="text-xl font-semibold mb-4">Quality Control</h3>
              <ul className="list-disc list-inside space-y-2 text-gray-600">
                <li>Product quality inspections</li>
                <li>Manufacturing process checks</li>
                <li>Equipment maintenance inspections</li>
                <li>Safety compliance audits</li>
              </ul>
            </div>
            <div className="bg-white p-8 rounded-lg shadow">
              <h3 className="text-xl font-semibold mb-4">Field Inspections</h3>
              <ul className="list-disc list-inside space-y-2 text-gray-600">
                <li>Site safety assessments</li>
                <li>Environmental compliance checks</li>
                <li>Property condition reports</li>
                <li>Construction site inspections</li>
              </ul>
            </div>
          </div>
        </section>
      </div>

      {/* CTA Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <section className="py-20">
          <div className="text-center">
            <h2 className="text-3xl font-bold mb-6">
              Ready to Digitize Your Inspection Process?
            </h2>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              Join organizations that have transformed their inspection processes with our digital solution.
            </p>
            <div className="space-x-4">
              <Link to="/request-demo">
                <Button className="bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors">
                  Request Demo
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" className="border border-blue-600 text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-blue-50 transition-colors">
                  Contact Sales
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default InspectionsUseCase;
